import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ProfileDialogService } from '../../services/profile-dialog.service';

/**
 * A person's name, anywhere it appears on an admin board, as the way into them.
 *
 * Every board holds uids: the events board's organizer, the engagement table's
 * participants, the abuse watchlist's accounts. Each one was a dead string,
 * and the reader who wanted to know who that was had to copy it into the users
 * list by hand. The events board had already grown a button for it, and that
 * button was about to be copied into four more templates, so it lives here
 * once instead.
 *
 * The dialog, not a route: these sit in filtered, sorted tables, and
 * navigating away to answer "who is that?" throws the filter away. The dialog
 * itself carries the links onward - the full case, the spaces they are in.
 *
 * A name with no uid behind it is rendered as plain text rather than as a
 * button that cannot do anything. An appeal from an address that matches no
 * account is the usual one.
 */
@Component({
  selector: 'app-person-link',
  standalone: true,
  imports: [CommonModule],
  template: `
    <button
      *ngIf="uid; else plain"
      type="button"
      class="person"
      [class.big]="big"
      [disabled]="busy"
      [title]="busy ? 'Opening…' : 'Open ' + label + ' — profile, spaces and case'"
      (click)="open($event)"
    >
      {{ label }}
    </button>
    <ng-template #plain><span class="person plain" [class.big]="big">{{ label }}</span></ng-template>
  `,
  styles: [
    `
      :host {
        display: inline;
      }

      .person {
        background: none;
        border: none;
        padding: 0;
        font: inherit;
        font-weight: 600;
        color: #1f6f66;
        cursor: pointer;
        text-align: left;
      }

      .person:hover {
        text-decoration: underline;
      }

      .person:disabled {
        color: #8f8c88;
        cursor: default;
        text-decoration: none;
      }

      .person.plain {
        color: inherit;
        cursor: default;
      }

      .person.big {
        font-size: 17px;
      }

      .person:focus-visible {
        outline: 2px solid #1f6f66;
        outline-offset: 2px;
        border-radius: 3px;
      }
    `,
  ],
})
export class PersonLinkComponent {
  /** The account to open. Without one the name is shown, but not as a link. */
  @Input() uid: string | null = null;
  /** What to call them. Falls back to a short uid, then to the dash. */
  @Input() name: string | null | undefined = null;
  /** Shown when there is neither a name nor a uid. */
  @Input() fallback = '—';
  /** The organizer panel on a space wants this at heading size. */
  @Input() big = false;

  /**
   * Why the profile could not be opened, in words fit to show. Boards have
   * their own notice line, so the message goes to them rather than to a
   * second error surface inside the table cell.
   */
  @Output() failed = new EventEmitter<string>();

  busy = false;

  constructor(private profileDialog: ProfileDialogService) {}

  get label(): string {
    return this.name?.trim() || this.uid?.slice(0, 8) || this.fallback;
  }

  open(event: MouseEvent): void {
    /* Rows and stat tiles around these are clickable in their own right;
       asking who somebody is is not a request to sort or filter. */
    event.stopPropagation();
    if (!this.uid || this.busy) return;
    this.busy = true;
    this.profileDialog
      .open(this.uid)
      .catch((err: Error) => this.failed.emit(err.message))
      .finally(() => (this.busy = false));
  }
}
